import * as React from 'react';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconNotificationsIcon} from 'shells/icon/svg/sidebar-icon-notifications';
// @ts-expect-error - imported from .js/.jsx file
import {SlackIcon} from 'shells/icon/svg/slack-icon';

import type {
    NextStepsFragment,
    NextStepsFragment_slackMessage,
    NextStepsFragment_nutshellNotification,
} from 'nutshell-graphql-types';

import {
    useDeleteNextStepsNutshellNotification,
    useDeleteNextStepsSlackMessage,
    useSetNextStepsNutshellNotification,
    useSetNextStepsSlackMessage,
    // @ts-expect-error
} from '../graphql/hooks';

import {
    getApiNextStepType,
    getInitialValuesFromNextSteps,
    getNextStepsCardBottomRow,
    getNextStepsCardTopRow,
    getSlackActionIdFromFormOrExistingNextStep,
    isNextStepUnconfigured,
} from './helpers';
import {NextStepsCard} from './next-steps-card';
import {NextStepsForm} from './next-steps-form';
import {NextStepsSidebarForm} from './next-steps-sidebar-form';
import type {NextStepType} from './types';

import styles from './next-steps-container.module.css';

type Props = {
    formId: string;
    description: string;
    bodyText: string;
    slackMessagePreviewMessage: React.ReactNode;
    slackMessagePreviewTopRowText?: string;
    nextSteps: NextStepsFragment;
    onlyAllowNotifyingSelf?: boolean;
    onUpdateSuccess: (arg1: {
        client: any;
        formId: string;
        nextStepsKey: 'slackMessage' | 'nutshellNotification';
        nextStep:
            | NextStepsFragment_slackMessage
            | NextStepsFragment_nutshellNotification
            | null
            | undefined;
    }) => void;
};
type NextStepsSidebar = {
    type: NextStepType | null | undefined;
    actionId: string | null | undefined;
    isOpen: boolean;
};

export function NextStepsContainer(props: Props) {
    const [nextStepsSidebar, setNextStepsSidebar] = React.useState<NextStepsSidebar>({
        type: null,
        actionId: null,
        isOpen: false,
    });
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = React.useState<
        string | null | undefined
    >(null);

    const setNextStepsSlackMessage = useSetNextStepsSlackMessage();
    const setNextStepsNutshellNotification = useSetNextStepsNutshellNotification();
    const deleteNextStepsSlackMessage = useDeleteNextStepsSlackMessage();
    const deleteNextStepsNutshellNotification = useDeleteNextStepsNutshellNotification();

    const saveNextStepSlack = (
        formId: string,
        channelId: string,
        actionId?: string | null
    ): Promise<any> => {
        return (
            setNextStepsSlackMessage({
                variables: {
                    input: {
                        nextStepsSlackMessage: {
                            formId,
                            channelId,
                            actionId,
                        },
                    },
                },
                // @ts-expect-error
                onUpdateSuccess: (proxy, mutationResults) => {
                    const nextStep =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.formNextStepsSlackMessageSet &&
                        mutationResults.data.formNextStepsSlackMessageSet.slackMessage;

                    if (!nextStep) return;

                    props.onUpdateSuccess({
                        client: proxy,
                        formId: props.formId,
                        nextStepsKey: 'slackMessage',
                        nextStep: nextStep,
                    });
                },
            })
                .then(() => {
                    setNextStepsSidebar({type: null, actionId: null, isOpen: false});
                })
                // @ts-expect-error
                .catch((error) => {
                    setSaveFailedErrorMessage(error.message);
                })
        );
    };

    const saveNextStepNutshellNotification = (
        formId: string,
        notifiables: any,
        actionId?: string | null
    ) => {
        return (
            setNextStepsNutshellNotification({
                variables: {
                    input: {
                        nextStepsNutshellNotification: {
                            formId,
                            notifiables,
                            actionId,
                        },
                    },
                },
                // @ts-expect-error
                onUpdateSuccess: (proxy, mutationResults) => {
                    const nextStep =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.formNextStepsNutshellNotificationSet &&
                        mutationResults.data.formNextStepsNutshellNotificationSet
                            .nutshellNotification;

                    if (!nextStep) return;

                    props.onUpdateSuccess({
                        client: proxy,
                        formId: props.formId,
                        nextStepsKey: 'nutshellNotification',
                        nextStep: nextStep,
                    });
                },
            })
                .then(() => {
                    setNextStepsSidebar({type: null, actionId: null, isOpen: false});
                })
                // @ts-expect-error
                .catch((error) => {
                    setSaveFailedErrorMessage(error.message);
                })
        );
    };

    const onDeleteNextStepNutshellNotification = (nextStepId: string) => {
        deleteNextStepsNutshellNotification({
            variables: {
                input: {
                    nextStepsNutshellNotification: {
                        actionId: nextStepId,
                    },
                },
            },
            // @ts-expect-error
            onUpdateSuccess: (proxy) =>
                props.onUpdateSuccess({
                    client: proxy,
                    formId: props.formId,
                    nextStepsKey: 'nutshellNotification',
                    nextStep: null,
                }),
        })
            .then(() => {
                setIsSaving(false);
                setNextStepsSidebar({
                    type: null,
                    actionId: null,
                    isOpen: false,
                });
            })
            // @ts-expect-error
            .catch((error) => {
                setSaveFailedErrorMessage(error.message);
            });
    };

    const onDeleteNextStepSlackMessage = (nextStepId: string) => {
        deleteNextStepsSlackMessage({
            variables: {
                input: {
                    nextStepsSlackMessage: {
                        actionId: nextStepId,
                    },
                },
            },
            onUpdateSuccess: (proxy: any) =>
                props.onUpdateSuccess({
                    client: proxy,
                    formId: props.formId,
                    nextStepsKey: 'slackMessage',
                    nextStep: null,
                }),
        })
            .then(() => {
                setIsSaving(false);
                setNextStepsSidebar({
                    type: null,
                    actionId: null,
                    isOpen: false,
                });
            })
            // @ts-expect-error
            .catch((error) => {
                setSaveFailedErrorMessage(error.message);
            });
    };

    const nextSteps = props.nextSteps;
    const {slackMessage, nutshellNotification} = nextSteps;
    const hasUnconfiguredNextSteps = Object.keys(nextSteps).some((nextStepType) => {
        return isNextStepUnconfigured(nextStepType, nextSteps);
    });

    return (
        <div className={styles['next-steps-container']}>
            {nutshellNotification && nutshellNotification.notifiables.length ? (
                <NextStepsCard
                    topRow={getNextStepsCardTopRow(props.onlyAllowNotifyingSelf)}
                    bottomRow={getNextStepsCardBottomRow(
                        nutshellNotification.notifiables,
                        props.onlyAllowNotifyingSelf
                    )}
                    icon={SidebarIconNotificationsIcon}
                    onClick={() =>
                        setNextStepsSidebar({
                            type: 'nutshellNotification',
                            actionId: nutshellNotification.id,
                            isOpen: true,
                        })
                    }
                />
            ) : undefined}
            {slackMessage && slackMessage.slackChannel ? (
                <NextStepsCard
                    topRow='Send a Slack notification'
                    bottomRow={slackMessage.slackChannel.display}
                    icon={SlackIcon}
                    onClick={() =>
                        setNextStepsSidebar({
                            type: 'slackMessage',
                            actionId: slackMessage.id,
                            isOpen: true,
                        })
                    }
                />
            ) : undefined}
            {hasUnconfiguredNextSteps ? (
                <div className={styles['add-next-step-container']}>
                    <EmptyAddCard
                        onClick={() =>
                            setNextStepsSidebar({type: null, actionId: null, isOpen: true})
                        }
                        text='Add a notification'
                        background='white'
                        size='small'
                        withoutIcon={true}
                        color={colors.greyLt}
                        isLoading={false}
                    />
                </div>
            ) : undefined}
            {nextStepsSidebar.isOpen ? (
                <NextStepsSidebarForm
                    // @ts-expect-error
                    title={nextStepsSidebar.type ? 'Edit notification' : 'Add a notification'}
                    onClose={() => {
                        setNextStepsSidebar({type: null, actionId: null, isOpen: false});
                        setSaveFailedErrorMessage(null);
                    }}
                    isSaving={isSaving}
                    deleteProps={
                        nextStepsSidebar.type && props.nextSteps[nextStepsSidebar.type]
                            ? {
                                  title: 'Delete next step',
                                  buttonText: 'Delete next step',
                                  message: 'Are you sure you’d like to delete this next step?',
                                  onDelete: () => {
                                      const nextStepType = nextStepsSidebar.type;
                                      const nextStep = nextStepType
                                          ? props.nextSteps[nextStepType]
                                          : undefined;

                                      setIsSaving(true);

                                      if (nextStepType === 'nutshellNotification' && nextStep) {
                                          onDeleteNextStepNutshellNotification(nextStep.id);
                                      } else if (nextStepType === 'slackMessage' && nextStep) {
                                          onDeleteNextStepSlackMessage(nextStep.id);
                                      }
                                  },
                              }
                            : undefined
                    }
                    saveFailedErrorMessage={saveFailedErrorMessage}
                    initialValues={getInitialValuesFromNextSteps(
                        props.nextSteps,
                        nextStepsSidebar.type
                    )}
                    isNewItem={
                        nextStepsSidebar.type && props.nextSteps[nextStepsSidebar.type]
                            ? // @ts-expect-error
                              props.nextSteps[nextStepsSidebar.type].id
                            : false
                    }
                    // @ts-expect-error
                    onSubmit={(formData) => {
                        // We either get the type from an existing card that
                        // was clicked on, or our redux-form state contains
                        // the type key which comes from the two-line-select
                        // of overall "NextStep" type selection
                        const nextStepType = formData.type || nextStepsSidebar.type;

                        if (getApiNextStepType(nextStepType) === 'nutshellNotification') {
                            return saveNextStepNutshellNotification(
                                props.formId,
                                formData.notifiables,
                                formData.id
                            );
                        } else if (nextStepType === 'slackMessage') {
                            const actionId = getSlackActionIdFromFormOrExistingNextStep(
                                formData.id,
                                props.nextSteps.slackMessage && props.nextSteps.slackMessage.id
                            );

                            return saveNextStepSlack(props.formId, formData.channelId, actionId);
                        }
                    }}
                >
                    <NextStepsForm
                        nextSteps={props.nextSteps}
                        startFromType={nextStepsSidebar.type}
                        description={props.description}
                        bodyText={props.bodyText}
                        slackMessagePreviewMessage={props.slackMessagePreviewMessage}
                        slackMessagePreviewTopRowText={props.slackMessagePreviewTopRowText}
                        onlyAllowNotifyingSelf={props.onlyAllowNotifyingSelf}
                    />
                </NextStepsSidebarForm>
            ) : undefined}
        </div>
    );
}
